import { useEffect } from 'react';
import { onAuthStateChanged } from '@firebase/auth';
import { useDispatch, useSelector } from 'react-redux';

import auth from './firebase';
import { signOut } from '@firebase/auth';
import { login, logout } from './store/userSlice';

import { setInitialUI } from './store/uiSlice';
import { clearFinancialSites } from './store/financialSlice';
import { clearHealthSites } from './store/healthSlice';

import SignInScreen from './pages/SignInScreen';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import { ToastContainer, toast } from 'react-toastify';

import SitePortalCard from './components/SiteDetails/SitePortalCard';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { Summary } from './components/Summary/Summary';
import { ThemeProvider } from '@mui/material/styles';
import themeEVC from './muiTheme_EVC';
import themeNCP from './muiTheme_NCP';
import Health from './components/Health/Health';
import LandingPage from './pages/LandingPage';
import PortalAccess from './components/PortalAccess/PortalAccess';
import Statements from './components/Statements/Statements';

import { PortalUsersRepository } from './repositories/portal_users_repository/portal_users_repository';

function App() {

  useEffect(() => {  
    if (window.location.hostname === 'evcportal.com' || window.location.hostname === 'www.evcportal.com') {
    return window.location.replace('https://roamportal.com');
    }
  }, []);

  const user = useSelector((state) => state.user.user);

  const { themeSelect } = useSelector((state) => state.user);
  const location = useLocation();
  const currentPath = location.pathname;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const protectedRoutes = ['/', '/site-detail', '/health', '/portal-access', '/statements'];
    const publicRoutes = ['/sign-in', '/forgot-password', '/reset-password', '/register'];

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        let portalUser;

        try {
          portalUser = await PortalUsersRepository.getPortalUser(user.uid);
        } catch (error) {
          toast.error(
            'Something went wrong with getting your user information. Please contact support.',
          );
          dispatch(logout());
          dispatch(clearFinancialSites());
          dispatch(setInitialUI());
          dispatch(clearHealthSites());
          signOut(auth);
          return;
        }

        auth.currentUser.getIdTokenResult().then((token) => {
          if (portalUser.portalStatus === 'approved' && portalUser.portfolios.length) {
            dispatch(
              login({
                uid: user.uid,
                email: user.email,
                token: token.token,
                //for now we will keep setting a single pid
                pid: portalUser.portfolios[0],
                portalStatus: portalUser.portalStatus,
                portalRole: portalUser.portalRole,
                firstName: portalUser.firstName,
                lastName: portalUser.lastName,
                viewStatements: portalUser.viewStatements,
              }),
            );
          } else if (portalUser.portalStatus === 'pending') {
            dispatch(
              login({
                uid: user.uid,
                email: user.email,
                token: token.token,
                portalStatus: portalUser.portalStatus,
              }),
            );
            dispatch(clearFinancialSites());
            dispatch(setInitialUI());
            dispatch(clearHealthSites());
          } else if (portalUser.portalStatus === 'denied') {
            toast.error('You do not have access to the Charge Portal');
            dispatch(logout());
            dispatch(clearFinancialSites());
            dispatch(setInitialUI());
            dispatch(clearHealthSites());
            signOut(auth);
          }
        });

        if (publicRoutes.includes(currentPath)) {
          navigate('/');
        }
      } else {
        dispatch(logout());
        dispatch(clearFinancialSites());
        dispatch(setInitialUI());
        dispatch(clearHealthSites());
        if (protectedRoutes.includes(currentPath)) {
          navigate('/sign-in');
        }
      }
    });
    return unsubscribe;
  }, [dispatch, currentPath, navigate]);

  return (
    <ThemeProvider theme={themeSelect === 'NCP' ? themeNCP : themeEVC}>
      <div className="w-full h-full bg-primary-background">
        <ToastContainer />
        {!user ? (
          // routes available when user isnt signed in
          <Routes>
            <Route path="/sign-in" element={<SignInScreen />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
          </Routes>
        ) : (
          <Routes>
            <Route path="/" element={<LandingPage />}>
              <Route path="/site-detail" element={<SitePortalCard />} />
              <Route path="/health" element={<Health />} />
              <Route path="/portal-access" element={<PortalAccess />} />
              <Route path="/statements" element={<Statements />} />
              <Route exact path="/" element={<Summary />} />
            </Route>
          </Routes>
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;
